import { fetchWithTimeout } from './fetchWithTimeout'
import { sleep } from './sleep'

type Schema<T> = {
  parse: (data: unknown) => T
}

export type RestApiClient = {
  /** request 実行 */
  run: <T>(params: {
    method: 'GET' | 'POST'
    path: string
    query?: Record<string, string>
    body?: Record<string, unknown>
    responseSchema: Schema<T>
  }) => Promise<T>
}

type BuildRequestData = (params: {
  method: 'GET' | 'POST'
  path: string
  query?: Record<string, string>
  body?: Record<string, unknown>
}) => Promise<{
  url: string
  headers?: Record<string, string>
  body?: string
}>

type GetDelayForRetry = (params: {
  statusCode: number
  errorMessage: string
}) => number | undefined

type ValidateResponse = (params: {
  data: unknown
}) =>
  | {
      status: 'error'
      error: unknown
    }
  | {
      status: 'retry'
      delay: number
      error: unknown
    }
  | {
      status: 'success'
    }

type CreateRestApiClient = (params: {
  /** REST API request に必要な url , headers , body を生成 */
  buildRequestData: BuildRequestData
  /** request 失敗時の response結果から retry の必要性及び retry 実行までの遅延時間を取得 / retry 不要の場合は undefined */
  getDelayForRetry: GetDelayForRetry
  /** response data の検証を行う */
  validateResponse?: ValidateResponse
  /** タイムアウト後の retry 実行までの遅延（待機）時間 */
  delayTimeout?: number
  /** リトライの最大回数 */
  maxRetry?: number
  /** request を proxy する場合の proxy base URL */
  proxy?: string
  /** リクエストのタイムアウト時間 */
  timeout?: number
}) => RestApiClient

export const createRestApiClient: CreateRestApiClient = ({
  buildRequestData,
  getDelayForRetry,
  validateResponse,
  delayTimeout = 1000, // デフォルトのタイムアウト後の遅延（待機）時間 = 1秒
  proxy,
  maxRetry = 3, // デフォルトのリトライ回数 = 3
  timeout = 5000, // デフォルトのタイムアウト = 5秒
}) => {
  const request = async <T>(params: {
    method: 'GET' | 'POST'
    path: string
    query?: Record<string, string>
    body?: Record<string, unknown>
    responseSchema: Schema<T>
    retryCount?: number
  }): Promise<T> => {
    const { method, responseSchema, retryCount = 0 } = params

    const { url, headers, body } = await buildRequestData(params)

    try {
      const proxyUrl = proxy ? `${proxy}${encodeURIComponent(url)}` : url
      const response = await fetchWithTimeout(
        proxyUrl,
        {
          method,
          headers,
          body,
        },
        timeout,
      )

      if (!response.ok) {
        const statusCode = response.status
        const text = await response.text()
        const errorMessage = text ?? response.statusText

        const delayForRetry = getDelayForRetry({
          statusCode,
          errorMessage,
        })
        if (retryCount < maxRetry && delayForRetry) {
          await sleep(delayForRetry)
          return request({
            ...params,
            retryCount: retryCount + 1,
          })
        }

        throw new Error(`${statusCode} ${errorMessage}`)
      }

      const data = await response.json()

      if (validateResponse) {
        const validationResult = validateResponse({ data })
        switch (validationResult.status) {
          case 'error': {
            throw validationResult.error
          }
          case 'retry': {
            if (retryCount < maxRetry) {
              await sleep(validationResult.delay)
              return request({
                ...params,
                retryCount: retryCount + 1,
              })
            }
            throw validationResult.error
          }
          case 'success': {
            break
          }
          default: {
            throw new Error(
              `Unknown status: ${validationResult satisfies never}`,
            )
          }
        }
      }

      return responseSchema.parse(data)
    } catch (error) {
      if (
        retryCount < maxRetry &&
        error instanceof Error &&
        (error.message.includes('fetch') || error.message.includes('timed out'))
      ) {
        await sleep(delayTimeout)
        return request({
          ...params,
          retryCount: retryCount + 1,
        })
      }
      throw error
    }
  }

  return {
    run: (params) =>
      request({
        ...params,
      }),
  }
}
